import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../redux/rootReducer";
import logoblack from "../../assets/logo_black.png";
import { login } from "../../redux/authSlice";
import { gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { NXEyes } from "../../icons";
import Cookies from 'js-cookie';

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      login {
        storeUrl
        token {
          access
          expiresIn
          refresh
          refreshExpiresIn
        }
        version
        baseCurrency
        user {
          email
          firstName
          fullName
          id
          lastName
          role
          username
        }
      }
    }
  }
`;

const LoginMain = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);

  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [isLoading, setIsLoading] = useState(false);
  const [domain, setDomain] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [isApiUrl, setIsApiUrl] = useState(false);
  const [step, setStep] = useState(1);
  const [apiHistory, setApiHistory] = useState<string[]>([]);

  // Improved validation regex
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](\.[a-zA-Z]{2,})+$/;
  const urlRegex = /^(https?:\/\/)?(localhost|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/.*)?$/;

  // Load API history on component mount
  useEffect(() => {
    const storedHistory = Cookies.get('apiHistory');
    if (storedHistory) {
      setApiHistory(JSON.parse(storedHistory));
    }

    const apiBase = Cookies.get('apiBase');
    if (apiBase) {
      setApiUrl(apiBase);
      setStep(2);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/dashboard");
    }
  }, [isLoggedIn, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const cleanedValue = value.trim().toLowerCase();
    setDomain(cleanedValue);

    // Automatically generate API URL based on domain
    if (domainRegex.test(cleanedValue)) {
      setApiUrl(`https://${cleanedValue}.salesbotics.io`);
    } else {
      setApiUrl("");
    }

    setError("");
  };

  const handleAPIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const cleanedValue = value.trim();

    setApiUrl(cleanedValue);

    // Extract domain from the API URL
    if (urlRegex.test(cleanedValue)) {
      const urlObj = new URL(
        cleanedValue.startsWith('http')
          ? cleanedValue
          : `http://${cleanedValue}`
      );

      // Handle localhost or other domains
      const extractedDomain = urlObj.hostname === 'localhost'
        ? 'localhost'
        : urlObj.hostname.split('.')[0];

      setDomain(extractedDomain);
    } else {
      setDomain("");
    }

    setError("");
  };

  const updateApiHistory = useCallback((url: string) => {
    const updatedHistory = Array.from(new Set([...apiHistory, url]));
    setApiHistory(updatedHistory);
    Cookies.set('apiHistory', JSON.stringify(updatedHistory));
  }, [apiHistory]);

  const removeFromApiHistory = useCallback((url: string) => {
    const updatedHistory = apiHistory.filter(item => item !== url);
    setApiHistory(updatedHistory);
    Cookies.set('apiHistory', JSON.stringify(updatedHistory));
  }, [apiHistory]);

  const handleContinue = () => {
    const isValid = isApiUrl
      ? urlRegex.test(apiUrl)
      : domainRegex.test(domain);

    if (isValid) {
      setStep(2);
      setError("");
      Cookies.set('apiBase', apiUrl);
      updateApiHistory(apiUrl);
      window.location.reload();
    } else {
      setError(isApiUrl
        ? "Please enter a valid API URL (e.g., https://example.com)"
        : "Please enter a valid domain name"
      );
    }
  };

  const handleContinueWithRecent = (url: string) => {
    setStep(2);
    setError("");
    setApiUrl(url);
    Cookies.set('apiBase', url);
    window.location.reload();
  };

  const handleBack = () => {
    setStep(1);
    setError("");
    Cookies.remove('apiBase');
  };

  const isFormValid = formData.email.includes("@") && formData.password.length >= 2;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await loginMutation({
        variables: { email: formData.email, password: formData.password }
      });
      dispatch(login(response.data.login.login));
      toast.success("Welcome back!");
    } catch (error: any) {
      setError(error.message);
      toast.error("Login failed: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-3xl shadow-2xl border border-gray-100 transform transition-all hover:scale-[1.02]">
        <div className="flex justify-center mb-6">
          <img src={logoblack} alt="NXTBN Logo" className="w-48 h-auto drop-shadow-md" />
        </div>

        <div className="text-center space-y-2 mb-6">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Welcome back</h2>
          <p className="text-gray-600 text-sm">Please enter your details to sign in</p>
        </div>

        <form onSubmit={step === 2 ? handleLogin : (e) => e.preventDefault()} className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              {/* API History Section */}
              {apiHistory.length > 0 && (
                <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-4">
                  <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-800">Recent Connections</h3>
                    <button 
                      type="button" 
                      className="text-xs text-[#277200] hover:underline"
                      onClick={() => {
                        setApiHistory([]);
                        Cookies.remove('apiHistory');
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {apiHistory.map((url, index) => (
                      <div 
                        key={url} 
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer group border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                        onClick={() => handleContinueWithRecent(url)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-[#277200]/10 rounded-full flex items-center justify-center">
                            <span className="text-[#277200] font-bold text-sm">
                              {url.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 group-hover:text-[#277200]">
                              {url.replace(/^https?:\/\//, '').replace('.salesbotics.io', '')}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromApiHistory(url);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label="Remove connection"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  {apiHistory.length > 4 && (
                    <div className="px-4 py-2 text-center border-t border-gray-100">
                      <button className="text-xs text-[#277200] hover:underline">
                        Show more connections
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!isApiUrl ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Domain Name
                    </label>
                    <input
                      type="text"
                      name="domain"
                      value={domain}
                      onChange={handleDomainChange}
                      className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-[#277200] focus:border-[#277200] transition-colors outline-none"
                      placeholder="Enter your domain (e.g., example)"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API URL
                    </label>
                    <input
                      type="text"
                      name="apiUrl"
                      value={apiUrl}
                      onChange={handleAPIChange}
                      className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-[#277200] focus:border-[#277200] transition-colors outline-none"
                      placeholder="Enter your API URL (e.g., localhost:8000)"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <button
                  type="button"
                  onClick={handleContinue}
                  className="w-full py-3 px-4 rounded-lg text-white font-medium bg-[#277200] hover:bg-opacity-90 active:bg-opacity-100 transition-colors shadow-md hover:shadow-lg"
                >
                  Continue
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsApiUrl(!isApiUrl);
                    // Reset fields when switching
                    setDomain("");
                    setApiUrl("");
                    setError("");
                  }}
                  className="w-full text-xs text-[#277200] underline hover:text-opacity-90 transition-colors"
                >
                  {isApiUrl ? "Or Login with Domain" : "Or Login with API URL"}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="relative bg-white rounded-xl shadow-md border border-gray-200 p-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="absolute top-3 right-3 
                    text-gray-400 hover:text-gray-600 
                    transition-all duration-300 
                    hover:rotate-90 hover:scale-110 
                    focus:outline-none focus:ring-2 focus:ring-[#277200]/50 rounded-full p-1"
                  aria-label="Close API Connection"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {!isApiUrl && (
                  <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-600">Shop</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {Cookies.get('apiBase')?.replace(/^https?:\/\//, '').replace('.salesbotics.io', '')}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-600">Store API</span>
                      <span className="text-sm font-semibold text-[#277200] truncate max-w-[200px]">
                        {Cookies.get('apiBase')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-[#277200] focus:border-[#277200] transition-colors outline-none"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-[#277200] focus:border-[#277200] transition-colors outline-none"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <NXEyes />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="remember-me"
                      className="w-4 h-4 rounded border-gray-300 text-[#277200] focus:ring-[#277200]"
                    />
                    <label htmlFor="remember-me" className="ml-2 text-sm text-gray-600">
                      Remember me
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || isLoading}
                  className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-all shadow-md hover:shadow-lg
                    ${isFormValid && !isLoading
                      ? "bg-[#277200] hover:bg-opacity-90 active:bg-opacity-100"
                      : "bg-gray-300 cursor-not-allowed"
                    }`}
                >
                  {isLoading ? "Signing in..." : "Sign in"}
                </button>
              </div>
            </div>
          )}
        </form>

        <p className="text-xs text-center text-gray-600 mt-4">
          Admin access required. If you don't have an account, please contact your administrator.
        </p>
      </div>
    </div>
  );
};

export default LoginMain;